import { computed, ref, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import {
  getTagConfigs,
  validarTag,
  isTagAllowedForTeam,
} from 'dashboard/api/centertel';

/**
 * Composable para gerenciar tags com regras Centertel:
 * - Filtro por time da conversa
 * - Validação via backend (bloqueio, dependências, exclusividade)
 * - Permissões por custom_role (contact_manage, conversation_manage)
 */
export function useCentertelTags() {
  const store = useStore();
  const {
    accountLabels,
    savedLabels,
    activeLabels,
    addLabelToConversation,
    removeLabelFromConversation,
  } = useConversationLabels();

  const tagConfigs = ref({});
  const isLoadingConfigs = ref(true);

  // ── Store data ──
  const currentChat = computed(() => store.getters.getSelectedChat);
  const currentUser = computed(() => store.getters.getCurrentUser);
  const currentRole = computed(() => store.getters.getCurrentRole);
  const currentCustomRoleId = computed(
    () => store.getters.getCurrentCustomRoleId
  );
  const customRoles = computed(
    () => store.getters['customRole/getCustomRoles']
  );

  // ── Permissões ──
  const isAdmin = computed(
    () => currentRole.value === 'administrator'
  );

  const userPermissions = computed(() => {
    if (isAdmin.value) {
      return {
        etiquetas: true,
        transferir: true,
        devolver: true,
        finalizar: true,
        reabrir: true,
        aceitar: true,
      };
    }
    // Buscar permissões do custom_role
    const roleId = currentCustomRoleId.value;
    if (!roleId || !customRoles.value.length) {
      // Vendedor padrão (sem custom_role)
      return {
        etiquetas: true,
        transferir: false,
        devolver: false,
        finalizar: true,   // pode finalizar apenas as conversas dele
        reabrir: false,
        aceitar: true,
      };
    }
    const myRole = customRoles.value.find(r => r.id === roleId);
    if (!myRole || !myRole.permissions) {
      return { etiquetas: true, transferir: false, devolver: false, finalizar: false, reabrir: false, aceitar: true };
    }
    const p = myRole.permissions;
    return {
      etiquetas:
        p.includes('contact_manage') || p.includes('conversation_manage'),
      transferir: p.includes('conversation_manage'),
      devolver: p.includes('conversation_manage'),
      finalizar: p.includes('conversation_manage'),
      reabrir: p.includes('conversation_manage'),
      aceitar: p.includes('conversation_unassigned_manage'),
    };
  });

  const canManageLabels = computed(() => userPermissions.value.etiquetas);

  // ── Time da conversa ──
  const conversationTeamId = computed(
    () => currentChat.value?.meta?.team?.id || null
  );

  // ── Labels filtradas por time ──
  const filteredLabels = computed(() => {
    const configs = tagConfigs.value;
    const teamId = conversationTeamId.value;
    return accountLabels.value.filter(label =>
      isTagAllowedForTeam(label.title, teamId, configs)
    );
  });

  /**
   * Labels disponíveis para adicionar (filtradas por time, excluindo ativas)
   */
  const availableLabels = computed(() => {
    const saved = savedLabels.value;
    return filteredLabels.value.filter(l => !saved.includes(l.title));
  });

  // ── Carregar configs ──
  onMounted(async () => {
    try {
      // Carregar custom_roles se não tiver
      if (!customRoles.value.length) {
        store.dispatch('customRole/getCustomRole');
      }
      tagConfigs.value = await getTagConfigs();
    } finally {
      isLoadingConfigs.value = false;
    }
  });

  /**
   * Adiciona tag com validação Centertel.
   * @returns {Promise<boolean>} true se adicionou, false se bloqueado
   */
  const addTagWithValidation = async label => {
    if (!canManageLabels.value) return false;

    const result = await validarTag(
      label.title,
      'adicionar',
      savedLabels.value,
      conversationTeamId.value
    );

    if (!result.permitido) {
      return { ok: false, mensagem: result.mensagem };
    }

    addLabelToConversation(label);
    return { ok: true, aviso: result.aviso || null };
  };

  /**
   * Remove tag com validação Centertel.
   * @returns {Promise<boolean>}
   */
  const removeTagWithValidation = async labelTitle => {
    if (!canManageLabels.value) return { ok: false, mensagem: 'Sem permissão' };

    const result = await validarTag(
      labelTitle,
      'remover',
      savedLabels.value,
      conversationTeamId.value
    );

    if (!result.permitido) {
      return { ok: false, mensagem: result.mensagem };
    }

    removeLabelFromConversation(labelTitle);
    return { ok: true };
  };

  return {
    // Labels
    activeLabels,
    availableLabels,
    savedLabels,
    tagConfigs,
    isLoadingConfigs,

    // Permissões
    isAdmin,
    userPermissions,
    canManageLabels,

    // Ações com validação
    addTagWithValidation,
    removeTagWithValidation,

    // Dados da conversa
    currentChat,
    currentUser,
    conversationTeamId,
  };
}
